/* ─────────────────────────────────────────────────────────────
   Sampling colours out of an image.

   One decode, one canvas, one getImageData, then cheap lookups.
   logoPixelOps.samplePixelColor does the same job but re-decodes
   the image and rebuilds a full-size canvas on every call, which
   is fine for a single pipette click and wasteful for building a
   palette — so this borrows its approach rather than the function.

   Deliberately no quantiser: picking dominant colours automatically
   is a separate piece of work, and nothing in the codebase does it
   today. This is the eyedropper half only.
─────────────────────────────────────────────────────────────── */

import { loadImage } from '../imageupload/imageCanvas';

/* The sampler downscales before reading pixels. Colour at a point
   does not need full resolution, and a phone photo at native size
   is a pointless 50MP allocation. */
const WORK_MAX_EDGE = 900;

/** Guard at selection time — useImageUpload only checks the encoded output. */
export const MAX_SOURCE_MB = 12;
export const ACCEPTED_TYPES = '.png,.jpg,.jpeg,.webp,.gif,.bmp';

export const checkFile = file => {
  if (!file) return 'No file';
  if (!file.type.startsWith('image/')) return 'That is not an image';
  if (file.size > MAX_SOURCE_MB * 1024 * 1024) {
    return `Image is ${(file.size / 1024 / 1024).toFixed(1)}MB — the limit is ${MAX_SOURCE_MB}MB`;
  }
  return null;
};

export const fileToDataUrl = file => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error('Could not read that file'));
  reader.readAsDataURL(file);
});

/**
 * Decode once and return a sampler over the result.
 *
 * @returns {Promise<{width:number,height:number,sampleAt:(relX:number,relY:number)=>{r,g,b}}>}
 *          `sampleAt` takes 0..1 relative coordinates, as produced by
 *          dividing a pointer offset by the element's bounding box.
 */
export async function loadSampler(src) {
  const img = await loadImage(src);
  const scale = Math.min(1, WORK_MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
  const width = Math.max(1, Math.round(img.naturalWidth * scale));
  const height = Math.max(1, Math.round(img.naturalHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, width, height);

  const { data } = ctx.getImageData(0, 0, width, height);

  const at = (x, y) => {
    const cx = Math.min(width - 1, Math.max(0, x));
    const cy = Math.min(height - 1, Math.max(0, y));
    const i = (cy * width + cx) * 4;
    return { r: data[i], g: data[i + 1], b: data[i + 2] };
  };

  const sampleAt = (relX, relY) => at(Math.round(relX * width), Math.round(relY * height));

  /**
   * The neighbourhood around a point, for a magnifier. Returns a
   * row-major (2*radius+1)^2 array with the sampled pixel at the
   * centre. Edges clamp rather than wrap, so the loupe stays square
   * at the borders instead of going ragged.
   */
  const sampleRegion = (relX, relY, radius = 4) => {
    const cx = Math.round(relX * width);
    const cy = Math.round(relY * height);
    const span = radius * 2 + 1;
    const pixels = [];
    for (let dy = -radius; dy <= radius; dy += 1) {
      for (let dx = -radius; dx <= radius; dx += 1) pixels.push(at(cx + dx, cy + dy));
    }
    return { span, pixels };
  };

  return {
    width,
    height,
    naturalWidth: img.naturalWidth,
    naturalHeight: img.naturalHeight,
    sampleAt,
    sampleRegion,
  };
}
